import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { darken, mix } from 'color2k'
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { LanguageCode } from '../i18n/types'
import { CLIENT_CONFIG } from './buildConfig'
import { touchSafeButtonVariants, touchSafeHover, touchSafeMenuItem } from './touchSafeTheme'

export interface KnowledgeSite {
  key: string
  name: string
  // Per-source agent tool identifier (`retrieve_knowledge_<key>`) the
  // assistant editor stores in an agent's `tools` list. Server-provided so
  // the frontend never re-derives the backend's name sanitization.
  tool_name: string
}

export interface ClientConfig {
  client_id: string
  // Static authentication switches. Runtime feature flags are deliberately not
  // here: FeaturesProvider fetches them from the backend.
  auth?: {
    // Username/password login next to Microsoft sign-in. Never a runtime flag.
    custom_login?: boolean
  }
  // Toggleable knowledge sources are fetched per-user via
  // useVisibleKnowledgeSites, not embedded here — a static build-config list
  // would leak restricted source names to ungated users.
  // Languages the switcher offers this client; absent falls back to the default set.
  languages?: LanguageCode[]
  theme: {
    name: string
    colors: {
      primary: string
      secondary: string
      accent: string
      background: string
      surface: string
      text: {
        primary: string
        secondary: string
        muted: string
      }
      success: string
      warning: string
      error: string
      // Login card overlay color. Defaults to the primary color at 75% opacity;
      // set this to pin an exact value independent of the primary derivation.
      loginCardOverlay?: string
      // Pale brand color backing hover and selected surfaces, replacing the
      // tint otherwise derived from the primary.
      tint?: string
    }
    branding: {
      logoUrl: string
      showLogoInHeader?: boolean
      // A wide wordmark needs a smaller value than the default, which is sized
      // for a roughly square mark.
      logoHeight?: string
      // Absent, logoUrl serves both color modes.
      logoDarkModeUrl?: string
      faviconUrl: string
      agentIconUrl?: string
      internalIconUrl?: string
      companyName: string
      // Display name of the assistant itself. Falls back to companyName-derived
      // naming when absent. Same identity as the vanilla agent name in bootstrap.py.
      assistantName?: string
      tagline: string
      supportEmail: string
      backgroundImage?: string
    }
    typography: {
      fontFamily: string
      headingFontFamily: string
      googleFontsUrl?: string
    }
    layout: {
      sidebarWidth: string
      headerHeight: string
      borderRadius: string
      loginCardOffsetY?: { base: string; md: string }
    }
    customTexts: {
      welcomeMessage: string
      chatPlaceholder: string
      footerText: string
      thinkingMessages?: Record<string, string[]>
      // Language-keyed copy shown when a user resolves to zero access scopes.
      noAccessMessage?: Record<string, string>
    }
    // Optional support block — when present, the information modal renders a
    // support section with these contact options.
    support?: {
      intro?: string
      items?: Array<{ label: string; value: string; href?: string }>
    }
    // Optional data visibility notice — when present, the information modal
    // renders this string under a translated title.
    dataVisibilityNotice?: string
    // Optional full override of the information modal content, keyed by language
    // code. When present, the modal renders entirely from this block for the
    // active language (falling back to English) and ignores the generic,
    // feature-flag-driven copy from the shared translations.
    informationModal?: Record<
      string,
      {
        title: string
        intro: string
        sections: Array<{ heading: string; items: string[] }>
        notes?: string[]
      }
    >
  }
}

interface ClientContextType {
  config: ClientConfig | null
  isLoading: boolean
  theme: any // Chakra UI theme object
}

const ClientContext = createContext<ClientContextType | undefined>(undefined)

export const useClient = () => {
  const context = useContext(ClientContext)
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider')
  }
  return context
}

interface ClientProviderProps {
  children: ReactNode
  clientId: string
}

// Build a Chakra shade palette from one base color. A positive amount is the
// fraction of the way to white: mixing rather than raising HSL lightness, which
// on a dark base only walks up the same hue at full saturation and never reaches
// a usable tint. A negative amount darkens by that much lightness, and 0 is the
// base color itself. Shades listed in `overrides` are pinned rather than derived.
const makePalette = (
  color: string,
  amounts: Record<number, number>,
  overrides: Record<number, string> = {},
) => ({
  ...Object.fromEntries(
    Object.entries(amounts).map(([shade, amount]) => [
      shade,
      amount === 0 ? color : amount > 0 ? mix(color, '#ffffff', amount) : darken(color, -amount),
    ]),
  ),
  ...overrides,
})

export const ClientProvider: React.FC<ClientProviderProps> = ({ children, clientId }) => {
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [theme, setTheme] = useState<any>(null)

  useEffect(() => {
    const loadBuildTimeConfig = async () => {
      try {
        setIsLoading(true)

        // Check for test config override
        const testConfig = (window as any).__TEST_CONFIG__
        const clientConfig = testConfig || (CLIENT_CONFIG as ClientConfig)

        if (testConfig) {
          console.log('Using test config override:', testConfig)
        }

        setConfig(clientConfig)

        if (clientConfig.theme.typography.googleFontsUrl) {
          const preconnect1 = document.createElement('link')
          preconnect1.rel = 'preconnect'
          preconnect1.href = 'https://fonts.googleapis.com'
          document.head.appendChild(preconnect1)

          const preconnect2 = document.createElement('link')
          preconnect2.rel = 'preconnect'
          preconnect2.href = 'https://fonts.gstatic.com'
          preconnect2.crossOrigin = 'anonymous'
          document.head.appendChild(preconnect2)

          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = clientConfig.theme.typography.googleFontsUrl
          document.head.appendChild(link)
        }

        // A brand that names its own pale color uses it for the two shades that
        // back hover and selected surfaces, in place of the derived tint.
        const tint = clientConfig.theme.colors.tint
        const tintShades: Record<number, string> = tint
          ? { 50: tint, 100: darken(tint, 0.07) }
          : {}

        // Create Chakra UI theme from client config
        const chakraTheme = extendTheme({
          styles: {
            global: {
              // The chat is a fixed-height shell whose message list scrolls on its
              // own; stop a swipe that reaches a scroll edge from chaining to the
              // document, which on mobile bounces the page and fires pull-to-refresh.
              'html, body': { overscrollBehavior: 'none' },
            },
          },
          colors: {
            brand: makePalette(clientConfig.theme.colors.primary, {
              50: 0.94,
              100: 0.86,
              200: 0.72,
              300: 0.5,
              400: 0.3,
              500: 0,
              600: -0.1,
              700: -0.2,
              800: -0.3,
              900: -0.4,
            }, tintShades),
            // Legacy aliases: many components still reference `primary` and
            // `colorScheme='primaryScheme'` from the removed clientTheme.ts.
            // Without these tokens Chakra resolves them to nothing and solid
            // buttons render invisible.
            primary: clientConfig.theme.colors.primary,
            primaryScheme: makePalette(clientConfig.theme.colors.primary, {
              50: 0.94,
              100: 0.86,
              200: 0.72,
              300: 0.5,
              400: 0.3,
              500: 0,
              600: -0.08,
              700: -0.16,
              800: -0.28,
              900: -0.4,
            }, tintShades),
            secondary: {
              500: clientConfig.theme.colors.secondary,
            },
            accent: {
              500: clientConfig.theme.colors.accent,
            },
            sidebar: {
              light: 'white',
              dark: 'gray.800',
            },
          },
          // `app.*` tokens are referenced by IconSelector, AgentForm,
          // ErrorState and LoadingState; without definitions Chakra emits the
          // literal token name as CSS (e.g. `background: app.bg`), which the
          // browser drops — surfaces as transparent popup/overlay backgrounds.
          semanticTokens: {
            colors: {
              // Hover / open-menu surfaces for neutral controls. Light mode is a
              // pale grey and a branded tint; dark mode steps only slightly up
              // from the gray.800 page so white text stays readable instead of
              // flashing a near-white block.
              buttonHover: { default: '#EDF2F7', _dark: 'gray.700' },
              buttonActive: {
                default: tint ?? mix(clientConfig.theme.colors.primary, '#ffffff', 0.85),
                _dark: 'gray.600',
              },
              'app.bg': { default: 'white', _dark: 'gray.800' },
              'app.bg.page': { default: 'gray.50', _dark: 'gray.900' },
              'app.bg.subtle': { default: 'gray.100', _dark: 'gray.700' },
              'app.bg.hover': { default: 'gray.50', _dark: 'gray.700' },
              'app.border': { default: 'gray.200', _dark: 'gray.600' },
              'app.text.primary': { default: 'gray.800', _dark: 'white' },
              'app.text.secondary': { default: 'gray.600', _dark: 'gray.300' },
              'app.text.muted': { default: 'gray.500', _dark: 'gray.400' },
            },
          },
          fonts: {
            heading: clientConfig.theme.typography.headingFontFamily,
            body: clientConfig.theme.typography.fontFamily,
          },
          radii: {
            base: clientConfig.theme.layout.borderRadius,
            md: clientConfig.theme.layout.borderRadius,
          },
          // Add llmTools configuration for KnowledgeSourceBadge
          llmTools: {
            none: {
              backgroundColor: '#FAD9E2',
              color: '#6D1D31',
            },
            sharepointSearch: {
              backgroundColor: '#C2EBF1',
              color: '#174A50',
            },
            internet: {
              backgroundColor: '#E2E8F0',
              textColor: '#1A202C',
            },
          },
          components: {
            Button: {
              defaultProps: {
                colorScheme: 'brand',
              },
              variants: {
                ...touchSafeButtonVariants,
                // Composer toolbar controls (the + and model-picker buttons): muted
                // resting color, a subtle grey on hover, a branded tint while their
                // menu is open (`_active` also matches `[data-active]`).
                inputControl: touchSafeHover(props => ({
                  color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
                  _hover: { bg: 'buttonHover' },
                  _active: { bg: 'buttonActive', color: props.colorMode === 'dark' ? 'white' : 'gray.800' },
                })),
              },
            },
            Menu: {
              baseStyle: {
                item: touchSafeMenuItem,
              },
            },
          },
        })
        setTheme(chakraTheme)

        // Update favicon and title
        updateFavicon(clientConfig.theme.branding.faviconUrl)
        document.title = `${clientConfig.theme.branding.assistantName || clientConfig.theme.branding.companyName} - AI Assistant`

        console.log(`Loaded build-time config for client: ${clientConfig.client_id}`, clientConfig)
      } catch (error) {
        console.error('Error loading build-time client config:', error)
        // Set default config on error
        setConfig(getDefaultConfig(clientId))
        setTheme(extendTheme({})) // Default Chakra theme
      } finally {
        setIsLoading(false)
      }
    }

    loadBuildTimeConfig()
  }, [clientId])

  console.log('config', config)

  const value: ClientContextType = {
    config,
    isLoading,
    theme,
  }

  if (isLoading || !theme) {
    return <div>Loading client configuration...</div>
  }

  return (
    <ClientContext.Provider value={value}>
      <ChakraProvider theme={theme}>{children}</ChakraProvider>
    </ClientContext.Provider>
  )
}

// Helper functions
function updateFavicon(faviconUrl: string) {
  const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement('link')
  link.type = 'image/x-icon'
  link.rel = 'shortcut icon'
  link.href = faviconUrl
  document.getElementsByTagName('head')[0].appendChild(link)
}

function getDefaultConfig(clientId: string): ClientConfig {
  return {
    client_id: clientId,
    theme: {
      name: 'Default',
      colors: {
        primary: '#3182CE',
        secondary: '#38B2AC',
        accent: '#ED8936',
        background: '#FFFFFF',
        surface: '#F7FAFC',
        text: {
          primary: '#2D3748',
          secondary: '#4A5568',
          muted: '#718096',
        },
        success: '#38A169',
        warning: '#D69E2E',
        error: '#E53E3E',
      },
      branding: {
        logoUrl: '/default-logo.png',
        faviconUrl: '/favicon.ico',
        companyName: 'AI Assistant',
        tagline: 'Powered by AI',
        supportEmail: 'support@example.com',
        backgroundImage: '/assets/static/images/background.svg',
      },
      typography: {
        fontFamily: 'Inter, system-ui, sans-serif',
        headingFontFamily: 'Inter, system-ui, sans-serif',
      },
      layout: {
        sidebarWidth: '280px',
        headerHeight: '64px',
        borderRadius: '8px',
      },
      customTexts: {
        welcomeMessage: 'Welcome to AI Assistant',
        chatPlaceholder: 'Ask me anything...',
        footerText: 'Powered by AI Technology',
      },
    },
  }
}
